(function () {
  'use strict';

  /* =========================================================
     CONFIG
  ========================================================= */

  const cfg = window.__cartDrawerConfig || {};

  const drawer = document.getElementById('cart-drawer');
  const overlay = document.getElementById('cart-drawer-overlay');
  const closeBtn = document.getElementById('cart-drawer-close');

  const itemsContainer = document.getElementById('cart-drawer-items');
  const emptyState = document.getElementById('cart-drawer-empty');
  const footer = document.getElementById('cart-drawer-footer');
  const continueShopping = document.getElementById('cart-continue-shopping');
  const itemTemplate = document.getElementById('cart-item-template');
  const subtotalValue = document.getElementById('cart-subtotal-value');
  const tierMessage = document.getElementById('cart-tier-message');
  const discountFeedback = document.getElementById('cart-discount-feedback');
  const titleCount = document.getElementById('cart-drawer-count');

  if (!drawer || !overlay || !itemsContainer || !itemTemplate) {
    return;
  }

  /* =========================================================
     STATE
  ========================================================= */

  let cartData = null;
  let busy = false;
  let renderSeq = 0;
  let lastAddAt = 0;

  const productCache = new Map();
  const nativeFetch = window.fetch.bind(window); /* un-hooked fetch for our own calls */

  /* =========================================================
     MONEY
  ========================================================= */

  function money(cents) {
    return '₹' + (Number(cents || 0) / 100).toLocaleString('en-IN', {
      maximumFractionDigits: 2
    });
  }

  /* =========================================================
     ESCAPE HTML
  ========================================================= */

  function escapeHtml(value) {
    return String(value || '').replace(/[&<>'"]/g, function (char) {
      return {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        "'": '&#39;',
        '"': '&quot;'
      }[char];
    });
  }

  /* =========================================================
     BUSY STATE
  ========================================================= */

  function setBusy(next) {
    busy = !!next;
    drawer.classList.toggle('is-busy', busy);

    drawer
      .querySelectorAll(
        '.cart-item__qty-btn, .cart-item__remove, .steal-deal-card__add-btn'
      )
      .forEach(function (button) {
        button.disabled = busy;
      });

    if (!busy) applyPlusLimits();
  }

  /* =========================================================
     FEEDBACK
  ========================================================= */

  function feedback(message, error) {
    if (!discountFeedback) return;
    discountFeedback.textContent = message || '';
    discountFeedback.classList.toggle('is-error', !!error);
  }

  /* =========================================================
     PRODUCT HANDLE
  ========================================================= */

  function getHandle(item) {
    if (item && item.handle) {
      return item.handle;
    }

    const match = String((item && item.url) || '').match(
      /\/products\/([^?/#]+)/
    );

    return match ? match[1] : '';
  }

  /* =========================================================
     PRODUCT META
  ========================================================= */

  async function getMeta(item) {
    const handle = getHandle(item);
    if (!handle) {
      return null;
    }

    if (!productCache.has(handle)) {
      productCache.set(
        handle,
        fetch('/products/' + encodeURIComponent(handle) + '.js', {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' }
        })
          .then(function (response) {
            return response.ok ? response.json() : null;
          })
          .catch(function () {
            return null;
          })
      );
    }

    const product = await productCache.get(handle);
    if (!product) {
      return null;
    }

    const variant = (product.variants || []).find(function (v) {
      return String(v.id) === String(item.variant_id);
    });

    if (!variant) {
      return null;
    }

    return {
      compareAt: Number(variant.compare_at_price || 0),
      variantTitle:
        variant.title && variant.title !== 'Default Title'
          ? variant.title
          : ''
    };
  }

  /* =========================================================
     RESIZE IMAGE
  ========================================================= */

  function resized(src) {
    if (!src) {
      return '';
    }
    return src.replace(/(\.[^.?]+)(\?.*)?$/, '_160x200$1$2');
  }

  /* =========================================================
     LOADING STATE
  ========================================================= */

  function showLoadingState() {
    if (emptyState) {
      emptyState.style.display = 'none';
    }

    if (footer) {
      footer.style.display = 'none';
    }

    itemsContainer.innerHTML =
      '<div class="cart-drawer__loading" role="status" aria-live="polite">' +
      '<span class="cart-drawer__spinner" aria-hidden="true"></span>' +
      '<span>Updating your bag…</span>' +
      '</div>';
  }

  /* =========================================================
     HEADER LIVE COUNTER
  ========================================================= */

  function updateHeaderCount(count) {
    const total = Number(count || 0);

    document.querySelectorAll('.clh-cart-count').forEach(function (element) {
      element.textContent = total;
      element.hidden = total === 0;
      element.setAttribute('aria-hidden', total === 0 ? 'true' : 'false');
      element.setAttribute(
        'aria-label',
        total + ' item' + (total === 1 ? '' : 's') + ' in cart'
      );
    });

    const selectors = [
      '.cart-count',
      '.header__cart-count',
      '[data-cart-count]',
      '#cart-count',
      '.cart-item-count',
      '.cart-link__bubble-num',
      '.cart-count-bubble',
      '.cart-count-bubble span'
    ];

    selectors.forEach(function (selector) {
      document.querySelectorAll(selector).forEach(function (element) {
        if (element.classList.contains('clh-cart-count')) {
          return;
        }
        element.textContent = total;
        element.style.display = total > 0 ? '' : 'none';
        element.setAttribute(
          'aria-label',
          total + ' item' + (total === 1 ? '' : 's') + ' in cart'
        );
      });
    });

    document
      .querySelectorAll('[data-cart-count-number]')
      .forEach(function (element) {
        element.textContent = total;
        element.style.display = total > 0 ? '' : 'none';
      });

    if (titleCount) {
      titleCount.textContent = total;
      titleCount.hidden = total === 0;
    }
  }

  /* =========================================================
     TIER / REWARD
  ========================================================= */

  function updateTier(qty) {
    if (!tierMessage) {
      return;
    }

    const tiers = Array.isArray(cfg.tierDiscounts)
      ? cfg.tierDiscounts.slice().sort(function (a, b) {
          return Number(a.minItems) - Number(b.minItems);
        })
      : [];

    let current = null;

    tiers.forEach(function (tier) {
      if (qty >= Number(tier.minItems || 0)) {
        current = tier;
      }
    });

    const next = tiers.find(function (tier) {
      return qty < Number(tier.minItems || 0);
    });

    document.querySelectorAll('.lux-tier').forEach(function (element) {
      element.classList.remove('is-active');
    });

    if (current) {
      const active = document.querySelector(
        '.lux-tier[data-tier="' + current.tier + '"]'
      );
      if (active) {
        active.classList.add('is-active');
      }
    }

    if (next) {
      const need = Math.max(1, Number(next.minItems || 0) - qty);
      tierMessage.innerHTML =
        'Add <strong>' +
        need +
        '</strong> more item' +
        (need === 1 ? '' : 's') +
        ' to unlock <strong>' +
        Number(next.discount || 0) +
        '% off</strong> with code <strong>' +
        escapeHtml(next.code) +
        '</strong>';
    } else if (current) {
      tierMessage.innerHTML =
        'Quantity offer unlocked: <strong>' +
        Number(current.discount || 0) +
        '% off</strong> with code <strong>' +
        escapeHtml(current.code) +
        '</strong>';
    } else {
      tierMessage.textContent = 'Add items to unlock your exclusive offer.';
    }
  }

  /* =========================================================
     SELLING SUBTOTAL
  ========================================================= */

  function sellingSubtotal(cart) {
    return (cart.items || []).reduce(function (sum, item) {
      const linePrice =
        item.original_line_price != null
          ? Number(item.original_line_price)
          : Number(item.original_price || item.price || 0) *
            Number(item.quantity || 0);

      return sum + linePrice;
    }, 0);
  }

  /* =========================================================
     DISCOUNT HELPERS
  ========================================================= */

  function appliedCodeObjects(cart) {
    return (cart.discount_codes || []).filter(function (discount) {
      return discount && discount.code && discount.applicable !== false;
    });
  }

  function allocationTitle(allocation) {
    const application = (allocation && allocation.discount_application) || {};
    return String(
      application.title ||
        application.code ||
        allocation.title ||
        allocation.code ||
        ''
    ).trim();
  }

  function allocationAmount(allocation) {
    return Number(
      allocation && allocation.amount != null
        ? allocation.amount
        : allocation && allocation.total_allocated_amount != null
        ? allocation.total_allocated_amount
        : 0
    );
  }

  function couponSavings(cart) {
    const codes = appliedCodeObjects(cart).map(function (discount) {
      return String(discount.code);
    });

    const wanted = new Set(
      codes.map(function (code) {
        return code.toLowerCase();
      })
    );

    const byCode = {};
    codes.forEach(function (code) {
      byCode[code.toLowerCase()] = 0;
    });

    let matched = 0;

    function addAllocation(allocation) {
      const amount = allocationAmount(allocation);
      const title = allocationTitle(allocation).toLowerCase();

      if (!amount || !title || !wanted.has(title)) {
        return;
      }

      byCode[title] = (byCode[title] || 0) + amount;
      matched += amount;
    }

    (cart.items || []).forEach(function (item) {
      (
        item.line_level_discount_allocations ||
        item.discount_allocations ||
        []
      ).forEach(addAllocation);
    });

    (cart.cart_level_discount_applications || []).forEach(function (
      application
    ) {
      const title = String(
        application && (application.title || application.code || '')
      )
        .trim()
        .toLowerCase();

      const amount = Number((application && application.total_allocated_amount) || 0);

      if (amount && title && wanted.has(title)) {
        byCode[title] = (byCode[title] || 0) + amount;
        matched += amount;
      }
    });

    if (codes.length === 1 && matched === 0) {
      const totalDiscount = Number(cart.total_discount || 0);
      const selling = sellingSubtotal(cart);
      const total = Number(cart.total_price || 0);
      const derived = Math.max(0, selling - total);
      const fallback = Math.max(totalDiscount, derived);

      if (fallback > 0) {
        byCode[codes[0].toLowerCase()] = fallback;
        matched = fallback;
      }
    }

    return {
      total: matched,
      byCode: byCode
    };
  }

  /* =========================================================
     SUMMARY
  ========================================================= */

  async function updateSummary(cart, sequence) {
    const items = cart.items || [];
    const selling = sellingSubtotal(cart);

    if (sequence !== renderSeq) {
      return;
    }

    if (subtotalValue) {
      const subtotal = Number(
        cart.items_subtotal_price != null ? cart.items_subtotal_price : selling
      );
      subtotalValue.textContent = money(subtotal);
    }
  }

  /* =========================================================
     CREATE CART ITEM
  ========================================================= */

  function itemNode(item, index) {
    const fragment = itemTemplate.content.cloneNode(true);
    const card = fragment.querySelector('.cart-item');

    if (!card) {
      return fragment;
    }

    const title = card.querySelector('.cart-item__title');
    const image = card.querySelector('.cart-item__image');
    const imageLink = card.querySelector('.cart-item__image-link');
    const titleLink = card.querySelector('.cart-item__title-link');
    const price = card.querySelector('.cart-item__price');
    const comparePrice = card.querySelector('.cart-item__compare-price');
    const variantTitleEl = card.querySelector(
      '.cart-item__variant-title, .cart-item__variant'
    );
    const quantityValue = card.querySelector('.cart-item__qty-value');
    const minusButton = card.querySelector('.cart-item__qty-minus');
    const plusButton = card.querySelector('.cart-item__qty-plus');
    const removeButton = card.querySelector('.cart-item__remove');

    card.dataset.lineIndex = String(index + 1);
    card.dataset.lineKey = item.key || '';

    if (title) {
      title.textContent = item.product_title || item.title || '';
    }

    if (item.url) {
      if (imageLink) imageLink.setAttribute('href', item.url);
      if (titleLink) titleLink.setAttribute('href', item.url);
    }

    if (image) {
      image.src = resized(item.image);
      image.alt = item.product_title || item.title || '';
    }

    const unitPrice = Number(item.original_price || item.price || 0);
    const finalPrice = Number(item.final_price || item.price || 0);

    if (price) {
      price.textContent = money(finalPrice);
    }

    if (comparePrice) {
      comparePrice.hidden = true;
      comparePrice.textContent = '';
    }

    /* Variant / size next to price */
    if (variantTitleEl) {
      const vTitle =
        item.variant_title && item.variant_title !== 'Default Title'
          ? item.variant_title
          : item.options_with_values
          ? item.options_with_values
              .map(function (o) {
                return o.value;
              })
              .join(' / ')
          : '';

      if (vTitle && vTitle !== 'Default Title') {
        variantTitleEl.textContent = 'Size: ' + vTitle;
        variantTitleEl.hidden = false;
        variantTitleEl.style.display = '';
      } else {
        variantTitleEl.textContent = '';
        variantTitleEl.hidden = true;
        variantTitleEl.style.display = 'none';
      }
    }

    if (quantityValue) {
      quantityValue.textContent = Number(item.quantity || 1);
    }

    if (minusButton) {
      minusButton.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        if (busy) return;
        changeLine(item.key, Math.max(0, currentLineQty(item.key) - 1));
      });
    }

    if (plusButton) {
      plusButton.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();

        if (busy) return;

        changeLine(item.key, currentLineQty(item.key) + 1);
      });
    }

    if (removeButton) {
      removeButton.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        if (busy) return;
        changeLine(item.key, 0);
      });
    }

    /* "+" limit: instant from page data, then refined with live stock */
    const pageLimit = limitFor(item.variant_id, null);
    card.dataset.maxQty = pageLimit === null ? '' : String(pageLimit);

    liveVariant(item.url, item.variant_id).then(function (live) {
      if (!document.body.contains(card)) return;
      const limit = limitFor(item.variant_id, live);
      card.dataset.maxQty = limit === null ? '' : String(limit);
      applyPlusLimits();
    });

    getMeta(item)
      .then(function (meta) {
        if (!meta || !document.body.contains(card)) return;
        if (comparePrice && meta.compareAt > unitPrice) {
          comparePrice.textContent = money(meta.compareAt);
          comparePrice.hidden = false;
        }
      })
      .catch(function (error) {
        console.error('[HousiousCart Meta]', error);
      });

    return fragment;
  }

  /* =========================================================
     RENDER CART
  ========================================================= */

  function render(cart) {
    cartData = cart;
    const sequence = ++renderSeq;
    itemsContainer.innerHTML = '';

    const hasItems = Array.isArray(cart.items) && cart.items.length > 0;
    const totalQuantity = Number(cart.item_count || 0);

    if (emptyState) {
      emptyState.style.display = hasItems ? 'none' : 'flex';
    }

    if (footer) {
      footer.style.display = hasItems ? 'block' : 'none';
    }

    if (hasItems) {
      cart.items.forEach(function (item, index) {
        itemsContainer.appendChild(itemNode(item, index));
      });
    }

    updateTier(totalQuantity);
    updateHeaderCount(totalQuantity);
    applyPlusLimits();

    updateSummary(cart, sequence).catch(function (error) {
      console.error('[HousiousCart Summary]', error);
    });
  }

  /* =========================================================
     FETCH CART
  ========================================================= */

  async function fetchCart() {
    const response = await fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    });

    if (!response.ok) {
      throw new Error('Could not load cart');
    }

    const cart = await response.json();
    render(cart);
    return cart;
  }

  /* =========================================================
     CHANGE QUANTITY
  ========================================================= */

  /* =========================================================
     CHANGE QUANTITY (With Inventory Protection)
  ========================================================= */

  async function changeLine(lineKey, quantity) {
    if (busy || !lineKey) return;
    quantity = Math.max(0, Number(quantity) || 0);

    const lineItem = ((cartData && cartData.items) || []).find(function (item) {
      return item.key === lineKey;
    });

    /* INCREASE: check live stock for this variant (all lines of it) first.
       DECREASE / REMOVE: always allowed, never blocked. */
    if (lineItem && quantity > Number(lineItem.quantity || 0)) {
      const live = await liveVariant(lineItem.url, lineItem.variant_id);
      const limit = limitFor(lineItem.variant_id, live);

      if (limit !== null) {
        const otherLines = cartData.items.reduce(function (sum, item) {
          return item.key !== lineKey &&
            String(item.variant_id) === String(lineItem.variant_id)
            ? sum + Number(item.quantity || 0)
            : sum;
        }, 0);

        if (quantity + otherLines > limit) {
          notify(
            limit <= 0
              ? 'This product is out of stock.'
              : 'Stock limit reached. Only ' + limit + ' available in stock.'
          );
          applyPlusLimits();
          return;
        }
      }
    }

    setBusy(true);

    const card = itemsContainer.querySelector(
      '[data-line-key="' + CSS.escape(String(lineKey)) + '"]'
    );
    if (card) {
      card.classList.add('is-updating');
    }

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          id: lineKey,
          quantity: Number(quantity)
        })
      });

      const cart = await response.json();
      if (!response.ok) {
        throw new Error(
          cart.description || cart.message || 'Could not update quantity'
        );
      }

      const changed = (cart.items || []).find(function (i) {
        return i.key === lineKey;
      });
      const gotQty = changed ? Number(changed.quantity) : 0;

      render(cart);
      updateHeaderCount(cart.item_count);

      if (quantity > 0 && gotQty < Number(quantity)) {
        feedback('Stock limit reached. Only ' + gotQty + ' available.', true);
      } else {
        feedback('', false);
      }

      document.dispatchEvent(
        new CustomEvent('cart:updated', { detail: { cart: cart } })
      );
    } catch (error) {
      console.error('[HousiousCart Quantity]', error);
      feedback(error.message || 'Stock limit reached or item unavailable.', true);
      await fetchCart().catch(function () {});
    } finally {
      setBusy(false);
    }
  }

  /* =========================================================
     STOCK HELPERS  (variant-wise inventory + cart quantity)

     Stock is read from, in this order:
       1. variant pills   (data-inventory / managed / policy)
       2. sticky bar JSON (#ozyProductVariants)
       3. radhe JSON      (script[data-radhe-variants])
       4. live product    (/products/{handle}.js) - always fresh
     limit === null  -> unlimited / unknown (Shopify server decides)
  ========================================================= */

  function parseJsonOnce(el) {
    if (!el) return [];
    if (!el.__parsed) {
      try {
        el.__parsed = JSON.parse(el.textContent);
      } catch (e) {
        el.__parsed = [];
      }
    }
    return el.__parsed;
  }

  function pageStock(variantId) {
    const id = String(variantId);
    const out = { known: false, available: null, limit: null };

    const pill = document.querySelector(
      '.housious-variant-pill[data-variant-id="' + id + '"]'
    );
    if (pill) {
      out.known = true;
      out.available = pill.dataset.available === 'true';
      if (
        pill.dataset.inventoryManaged === 'true' &&
        (pill.dataset.inventoryPolicy || 'deny') !== 'continue'
      ) {
        out.limit = Math.max(0, parseInt(pill.dataset.inventory, 10) || 0);
      }
    } else {
      let found = null;

      parseJsonOnce(document.getElementById('ozyProductVariants')).some(function (v) {
        if (String(v.id) !== id) return false;
        found = {
          available: v.available !== false,
          limit:
            v.inventory_management && v.inventory_policy !== 'continue'
              ? Math.max(0, Number(v.inventory_quantity) || 0)
              : null
        };
        return true;
      });

      if (!found) {
        document.querySelectorAll('script[data-radhe-variants]').forEach(function (el) {
          if (found) return;
          parseJsonOnce(el).some(function (v) {
            if (String(v.id) !== id) return false;
            found = {
              available: v.available !== false,
              limit: Number(v.max) < 99 ? Number(v.max) : null
            };
            return true;
          });
        });
      }

      if (found) {
        out.known = true;
        out.available = found.available;
        out.limit = found.limit;
      }
    }

    if (out.available === false) out.limit = 0;
    return out;
  }

  async function liveVariant(source, variantId) {
    const text = String(source || '');
    const match = text.match(/\/products\/([^?/#]+)/);
    const handle = match ? match[1] : text;
    if (!handle) return null;

    try {
      const product = await readJson('/products/' + encodeURIComponent(handle) + '.js');
      const variant = (product.variants || []).find(function (v) {
        return String(v.id) === String(variantId);
      });
      if (!variant) return null;

      return {
        available: variant.available !== false,
        managed: !!variant.inventory_management,
        qty: typeof variant.inventory_quantity === 'number' ? variant.inventory_quantity : null
      };
    } catch (e) {
      return null;
    }
  }

  /* highest total quantity of this variant allowed in the cart (null = no limit known) */
  function limitFor(variantId, live) {
    const page = pageStock(variantId);
    if (page.available === false) return 0;

    let limit = page.limit;

    if (live) {
      if (!live.available) return 0;
      if (live.managed && live.qty !== null) {
        if (limit !== null) {
          limit = Math.min(limit, Math.max(0, live.qty));
        } else if (!page.known && live.qty > 0) {
          limit = live.qty;
        }
      }
    }
    return limit;
  }

  function stockMessage(limit, inCart, quantity) {
    if (limit <= 0) return 'This product is out of stock.';
    if (inCart >= limit) {
      return 'Stock limit reached. You already have all ' + limit + ' piece' + (limit === 1 ? '' : 's') + ' in your cart.';
    }
    if (inCart + quantity > limit) {
      return 'Only ' + limit + ' available. You have ' + inCart + ' in cart (you can add ' + (limit - inCart) + ' more).';
    }
    return '';
  }

  function cartQtyForVariant(cart, variantId) {
    return ((cart && cart.items) || []).reduce(function (sum, item) {
      return String(item.variant_id) === String(variantId)
        ? sum + Number(item.quantity || 0)
        : sum;
    }, 0);
  }

  async function readJson(url) {
    const response = await nativeFetch(url, {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error('Request failed: ' + url);
    return response.json();
  }

  /* small toast so a blocked add is always visible, on any page */
  function notify(message) {
    if (!message) return;
    feedback(message, true);

    let toast = document.getElementById('cart-stock-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'cart-stock-toast';
      toast.setAttribute('role', 'alert');
      toast.style.cssText =
        'position:fixed;left:50%;bottom:90px;transform:translateX(-50%);max-width:90vw;' +
        'padding:12px 18px;background:#1a1a1a;color:#fff;font-size:14px;line-height:1.4;' +
        'border-radius:8px;z-index:2147483647;box-shadow:0 6px 24px rgba(0,0,0,.25);' +
        'text-align:center;transition:opacity .2s;';
      document.body.appendChild(toast);
    }
    toast.textContent = message;
    toast.style.opacity = '1';
    toast.style.display = 'block';
    clearTimeout(toast.__t);
    toast.__t = setTimeout(function () {
      toast.style.opacity = '0';
      toast.style.display = 'none';
    }, 3800);

    document.dispatchEvent(new CustomEvent('cart:stock-limit', { detail: { message: message } }));
  }

  function currentLineQty(lineKey) {
    const line = ((cartData && cartData.items) || []).find(function (i) {
      return i.key === lineKey;
    });
    return line ? Number(line.quantity || 0) : 0;
  }

  function cartQty(variantId) {
    return cartQtyForVariant(cartData, variantId);
  }

  /* disable "+" in the drawer when the variant already has all its stock in the cart */
  function applyPlusLimits() {
    const items = (cartData && cartData.items) || [];

    itemsContainer.querySelectorAll('.cart-item').forEach(function (card) {
      const plus = card.querySelector('.cart-item__qty-plus');
      const max = card.dataset.maxQty;
      if (!plus || max === undefined || max === '') return;

      const item = items.find(function (i) {
        return i.key === card.dataset.lineKey;
      });
      if (!item) return;

      const total = cartQtyForVariant(cartData, item.variant_id);
      const atLimit = total >= Number(max);

      plus.classList.toggle('is-at-limit', atLimit);
      plus.setAttribute('aria-disabled', atLimit ? 'true' : 'false');
      if (!busy) plus.disabled = atLimit;
    });
  }

  /* =========================================================
     SAFE ADD TO CART  (single gate for EVERY add-to-cart button)

     Order of work (nothing is added / counted before step 4):
       1. Read live stock   -> /products/{handle}.js
       2. Read live cart    -> /cart.js
       3. Compare           -> (qty already in cart + qty requested) vs stock
       4. ONLY if allowed   -> POST /cart/add.js
       5. Re-read cart      -> render drawer, update header count, open drawer

     Uses nativeFetch so our own calls are not seen by the fetch hook.
  ========================================================= */

  let adding = false;

  /*
    opts:
      handle        product handle (or pass productUrl)
      productUrl    any URL containing /products/{handle}
      maxQty        upper limit rendered by Liquid for this variant
                    (null / undefined = unlimited)
      openDrawer    default true
    returns { ok, message, cart }
  */
  async function safeAdd(variantId, quantity, opts) {
    opts = opts || {};
    variantId = Number(variantId);
    quantity = Math.max(1, parseInt(quantity, 10) || 1);

    if (!variantId) return { ok: false, message: 'Please select a variant.' };
    if (adding || busy) return { ok: false, message: 'Please wait…', silent: true };

    adding = true;
    setBusy(true);

    try {
      /* 1) LIVE STOCK for THIS variant */
      const source = opts.handle ? '/products/' + opts.handle : opts.productUrl;
      const live = await liveVariant(source, variantId);
      let limit = limitFor(variantId, live);

      if (typeof opts.maxQty === 'number' && isFinite(opts.maxQty)) {
        limit = limit === null ? opts.maxQty : Math.min(limit, opts.maxQty);
      }

      /* 2) LIVE CART: how many of THIS variant are already in the cart */
      const liveCart = await readJson('/cart.js');
      const inCart = cartQtyForVariant(liveCart, variantId);

      /* 3) COMPARE - before anything is added */
      if (limit !== null) {
        const message = stockMessage(limit, inCart, quantity);
        if (message) return { ok: false, message: message };
      }

      /* 4) ADD - only now does the quantity change */
      const addResponse = await nativeFetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: quantity })
      });
      const addData = await addResponse.json().catch(function () { return {}; });

      if (!addResponse.ok) {
        return {
          ok: false,
          message: addData.description || addData.message || 'Could not add product.'
        };
      }

      /* 5) SYNC UI - now the number increases */
      lastAddAt = Date.now();
      const cart = await readJson('/cart.js');
      render(cart);
      updateHeaderCount(cart.item_count);
      feedback('', false);

      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));

      if (opts.openDrawer !== false) {
        await openDrawer(false);
      }

      return { ok: true, message: '', cart: cart };
    } catch (error) {
      console.error('[CartDrawer safeAdd]', error);
      return { ok: false, message: error.message || 'Could not add product.' };
    } finally {
      adding = false;
      setBusy(false);
    }
  }

  /* kept for older callers: same validated path */
  async function addToCart(variantId, quantity, options) {
    options = options || {};
    const result = await safeAdd(variantId, quantity, {
      productUrl: options.productUrl,
      handle: options.handle,
      maxQty: options.maxQty,
      openDrawer: options.autoOpenDrawer !== false
    });
    if (!result.ok && !result.silent) {
      notify(result.message);
    }
    return result;
  }

  /* =========================================================
     OPEN / CLOSE DRAWER
  ========================================================= */

  async function openDrawer(fetchFirst = true) {
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');

    overlay.classList.add('is-active');
    overlay.setAttribute('aria-hidden', 'false');

    document.body.classList.add('cart-drawer-open');

    if (fetchFirst) {
      await fetchCart().catch(function (error) {
        console.error('[HousiousCart Fetch]', error);
      });
    }
  }

  function closeDrawer() {
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');

    overlay.classList.remove('is-active');
    overlay.setAttribute('aria-hidden', 'true');

    document.body.classList.remove('cart-drawer-open');
  }

  /* =========================================================
     INTERCEPT CART LINKS AND BUTTONS
     (Prevents redirecting to /cart page)
  ========================================================= */

  document.addEventListener('click', function (event) {
    const trigger = event.target.closest(
      '[data-cart-toggle], a[href="/cart"], a[href$="/cart"]'
    );
    if (!trigger) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    openDrawer(true);
  });

  /* =========================================================
     ADD TO CART FORM INTERCEPTOR (fallback for other forms)
     Product page / sticky bar / product cards handle their own
     submit and call CartDrawer.safeAdd, so they are skipped here.
     Every add still goes through safeAdd (stock check first).
  ========================================================= */

  function formHandle(form) {
    const holder = form.closest('[data-product-handle]');
    if (holder) return holder.getAttribute('data-product-handle');
    const link = (form.closest('article, li, .product-card, [data-product-id]') || form)
      .querySelector('a[href*="/products/"]');
    return link ? link.getAttribute('href') : '';
  }

  function ownedByOtherHandler(form) {
    return !!(form.closest('[data-radhe-actions]') || form.id === 'ozyStickyForm');
  }

  async function handleFormAdd(form) {
    const idInput = form.querySelector('[name="id"]');
    const quantityInput = form.querySelector('[name="quantity"]');
    const variantId = Number(idInput ? idInput.value : 0);
    const quantity = Number(quantityInput ? quantityInput.value : 1) || 1;
    if (!variantId) return;

    const result = await safeAdd(variantId, quantity, { productUrl: formHandle(form) });
    if (!result.ok && !result.silent) {
      notify(result.message);
    }
  }

  document.addEventListener('submit', function (event) {
    const form = event.target.closest(
      '[data-type="add-to-cart-form"], form[action*="/cart/add"]'
    );
    if (!form || ownedByOtherHandler(form)) {
      return;
    }

    event.preventDefault();
    handleFormAdd(form);
  });

  if (closeBtn) {
    closeBtn.addEventListener('click', closeDrawer);
  }

  overlay.addEventListener('click', closeDrawer);

  if (continueShopping) {
    continueShopping.addEventListener('click', closeDrawer);
  }

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' && drawer.classList.contains('is-open')) {
      closeDrawer();
    }
  });

  /* =========================================================
     GLOBAL ADD-TO-CART HOOK
     Wishlist / quick-add / kisi bhi app ka add-to-cart
     -> cart page par nahi jayega, drawer auto open hoga
  ========================================================= */

  function isAddUrl(url) {
    return /\/cart\/add(\.js|\.json)?(\?|#|$)/.test(String(url || ''));
  }

  function openAfterExternalAdd() {
    openDrawer(true);
  }

  /* 1) fetch() hook
        Covers EVERY other script that calls /cart/add (theme code, apps,
        quick-add). Stock is checked BEFORE the request is sent; if it is
        over the limit the request never reaches Shopify, so the quantity
        cannot increase. Our own calls use nativeFetch and skip this. */

  async function bodyToItems(input, init) {
    let body = init && init.body;

    if (body === undefined && input && typeof input === 'object' && typeof input.clone === 'function') {
      try {
        body = await input.clone().text();
      } catch (e) {
        body = null;
      }
    }
    if (!body) return [];

    const items = [];

    function fromObject(obj) {
      if (!obj) return;
      if (Array.isArray(obj.items)) {
        obj.items.forEach(function (i) {
          items.push({ id: i.id, quantity: i.quantity });
        });
      } else if (obj.id) {
        items.push({ id: obj.id, quantity: obj.quantity });
      }
    }

    function fromParams(params) {
      const grouped = {};
      params.forEach(function (value, key) {
        const m = key.match(/^items\[(\d+)\]\[(id|quantity)\]$/);
        if (m) {
          grouped[m[1]] = grouped[m[1]] || {};
          grouped[m[1]][m[2]] = value;
        }
      });
      const keys = Object.keys(grouped);
      if (keys.length) {
        keys.forEach(function (k) { items.push(grouped[k]); });
      } else if (params.get('id')) {
        items.push({ id: params.get('id'), quantity: params.get('quantity') });
      }
    }

    try {
      if (typeof FormData !== 'undefined' && body instanceof FormData) {
        const params = new URLSearchParams();
        body.forEach(function (value, key) {
          if (typeof value === 'string') params.append(key, value);
        });
        fromParams(params);
      } else if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
        fromParams(body);
      } else if (typeof body === 'string') {
        try {
          fromObject(JSON.parse(body));
        } catch (e) {
          fromParams(new URLSearchParams(body));
        }
      }
    } catch (e) {}

    return items;
  }

  async function externalAddVerdict(items) {
    const wanted = {};
    items.forEach(function (item) {
      const id = String(item.id);
      wanted[id] = (wanted[id] || 0) + (parseInt(item.quantity, 10) || 1);
    });

    let cart = null;

    for (const id of Object.keys(wanted)) {
      const limit = limitFor(id, null);
      if (limit === null) continue;

      if (!cart) cart = await readJson('/cart.js');
      const message = stockMessage(limit, cartQtyForVariant(cart, id), wanted[id]);
      if (message) return { ok: false, message: message };
    }
    return { ok: true };
  }

  window.fetch = async function (input, init) {
    let url = '';
    if (typeof input === 'string') {
      url = input;
    } else if (input && input.url) {
      url = input.url;
    } else if (input) {
      url = String(input);
    }

    if (!isAddUrl(url)) {
      return nativeFetch(input, init);
    }

    try {
      const items = await bodyToItems(input, init);
      if (items.length) {
        const verdict = await externalAddVerdict(items);
        if (!verdict.ok) {
          notify(verdict.message);
          return new Response(
            JSON.stringify({
              status: 422,
              message: 'Cart Error',
              description: verdict.message
            }),
            { status: 422, headers: { 'Content-Type': 'application/json' } }
          );
        }
      }
    } catch (error) {
      console.warn('[CartDrawer] stock guard skipped', error);
    }

    lastAddAt = Date.now();
    const response = await nativeFetch(input, init);
    if (response && response.ok) openAfterExternalAdd();
    return response;
  };

  /* 2) XMLHttpRequest hook (kuch apps fetch ki jagah XHR use karte hain) */
  const nativeXhrOpen = XMLHttpRequest.prototype.open;
  const nativeXhrSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (method, url) {
    this.__cdIsAdd = isAddUrl(url);
    return nativeXhrOpen.apply(this, arguments);
  };

  XMLHttpRequest.prototype.send = function () {
    if (this.__cdIsAdd) {
      const internal = busy;
      lastAddAt = Date.now();
      if (!internal) {
        this.addEventListener('load', function () {
          if (this.status >= 200 && this.status < 300) openAfterExternalAdd();
        });
      }
    }
    return nativeXhrSend.apply(this, arguments);
  };

  /* 3) form.submit() (programmatic) - isme "submit" event fire nahi hota */
  const nativeFormSubmit = HTMLFormElement.prototype.submit;

  HTMLFormElement.prototype.submit = function () {
    const action = this.getAttribute('action') || '';

    if (/\/cart\/add/.test(action) && !ownedByOtherHandler(this)) {
      handleFormAdd(this);
      return;
    }

    return nativeFormSubmit.apply(this, arguments);
  };

  /* 4) Add ke turant baad app /cart par redirect kare to rok do
        (Chrome / Edge / Safari 26+ - Navigation API) */
  if (window.navigation && typeof window.navigation.addEventListener === 'function') {
    window.navigation.addEventListener('navigate', function (event) {
      try {
        const dest = new URL(event.destination.url);

        if (
          dest.origin === window.location.origin &&
          /^\/cart\/?$/.test(dest.pathname) &&
          Date.now() - lastAddAt < 5000 &&
          event.cancelable &&
          !event.hashChange &&
          event.navigationType !== 'traverse'
        ) {
          event.preventDefault();
          openDrawer(true);
        }
      } catch (error) {}
    });
  }

  /* =========================================================
     PUBLIC CART API
  ========================================================= */

  window.CartDrawer = {
    open: function () {
      return openDrawer(true);
    },
    close: closeDrawer,
    addToCart: addToCart,
    safeAdd: safeAdd,
    getCartQty: cartQty,
    getLimit: function (variantId) {
      return limitFor(variantId, null);
    },
    fetchCart: fetchCart,
    changeLine: changeLine,
    updateHeaderCount: updateHeaderCount
  };

  /* =========================================================
     INITIALIZE COUNT
  ========================================================= */

  nativeFetch('/cart.js', {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' }
  })
    .then(function (response) {
      return response.ok ? response.json() : Promise.reject();
    })
    .then(function (cart) {
      cartData = cart;
      updateHeaderCount(cart.item_count);
      document.dispatchEvent(new CustomEvent('cart-drawer:ready', { detail: { cart: cart } }));
    })
    .catch(function () {});
})();